import React, { useEffect, useRef, useState } from 'react';
import {
  X, Calendar, CalendarDays, CheckSquare, LayoutList, Columns3,
  Settings, Search, LogOut, EyeOff, ChevronDown, Users, Plus, RefreshCw,
  MoreVertical,
} from 'lucide-react';
import { Logo } from '../../ui/Logo';
import { Calendar as CalendarType } from '../../../types';
import { useSpace } from '../../../contexts/SpaceContext';
import { useAuth } from '../../../contexts/AuthContext';


interface ImportedCalendarInfo {
  id: string;
  name: string;
  url: string;
  color: string;
  enabled: boolean;
}

interface MobileDrawerProps {
  isOpen: boolean;
  onClose: () => void;

  // View
  viewMode: string;
  onViewModeChange: (mode: 'year' | 'month' | 'week' | 'schedule' | 'todo') => void;

  // Settings
  onOpenSettings: () => void;

  // Search
  searchQuery: string;
  onSearchChange: (query: string) => void;

  // Tags
  calendars: CalendarType[];
  selectedCalendarFilters: Set<string>;
  onToggleCalendarFilter: (tagId: string) => void;
  onSelectAllTags: () => void;
  onOpenCategoryMenu?: (tag: { id?: string; name: string; color: string; is_shared?: boolean; share_token?: string | null }, anchor: HTMLElement, isNew: boolean) => void;

  // Imported calendars
  importedCalendars?: ImportedCalendarInfo[];
  onToggleCalendar?: (id: string) => void;
  onShowAddCalendarModal?: () => void;
  onSyncCalendar?: (id: string) => Promise<void>;
  syncing?: string | null;
  onOpenCalendarMenu?: (calendar: { id: string; name: string; url: string; color: string }, anchor: HTMLElement) => void;

  // Sign out
  onSignOut?: () => void;
}

const VIEW_MODES = [
  { mode: 'year' as const, label: 'Year', icon: Columns3 },
  { mode: 'month' as const, label: 'Month', icon: Calendar },
  { mode: 'week' as const, label: 'Week', icon: CalendarDays },
  { mode: 'schedule' as const, label: 'Schedule', icon: LayoutList },
  { mode: 'todo' as const, label: 'Tasks', icon: CheckSquare },
];

export const MobileDrawer: React.FC<MobileDrawerProps> = ({
  isOpen,
  onClose,
  viewMode,
  onViewModeChange,
  onOpenSettings,
  searchQuery,
  onSearchChange,
  calendars,
  selectedCalendarFilters,
  onToggleCalendarFilter,
  onSelectAllTags,
  importedCalendars,
  onToggleCalendar,
  onShowAddCalendarModal,
  onSyncCalendar,
  syncing,
  onOpenCategoryMenu,
  onOpenCalendarMenu,
  onSignOut,
}) => {
  const { user } = useAuth();
  const { currentSpace, setCurrentSpace, spaces, isPersonalSpace } = useSpace();
  const [showSpacePicker, setShowSpacePicker] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const personalSpaceName = user?.user_metadata?.display_name || user?.email?.split('@')[0] || 'Personal';
  const currentSpaceName = isPersonalSpace ? personalSpaceName : currentSpace?.name || 'Calendar';

  // Close on ESC
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const allSelected = calendars.length > 0 && calendars.every(t => selectedCalendarFilters.has(t.id));

  return (
    <>
      {/* Backdrop */}
      <div
        className="sm:hidden fixed inset-0 z-50 bg-black/40"
        style={{ animation: 'fadeInBackdrop 200ms ease both' }}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        className="sm:hidden fixed top-0 bottom-0 left-0 z-50 w-[280px] bg-[var(--surface-primary)] flex flex-col"
        style={{
          boxShadow: '4px 0 24px rgba(0,0,0,0.15)',
          animation: 'drawerSlideIn 250ms cubic-bezier(0.16,1,0.3,1) both',
          paddingTop: 'env(safe-area-inset-top)',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        {/* Header */}
        <div className="flex items-center px-4 py-3 border-b border-[var(--border-primary)]">
          <Logo size="sm" showText />
        </div>

        {/* Space switcher */}
        {spaces.length > 0 && (
          <div className="px-3 pt-2 pb-1">
            <button
              onClick={() => setShowSpacePicker(!showSpacePicker)}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--surface-tertiary)] active:bg-[var(--sidebar-hover)] transition-colors"
            >
              <div className="w-6 h-6 rounded-md bg-[var(--accent-subtle)] flex items-center justify-center flex-shrink-0">
                <Users className="w-3.5 h-3.5 text-[var(--accent-primary)]" />
              </div>
              <span className="flex-1 text-left text-sm font-medium text-[var(--content-primary)] truncate">
                {currentSpaceName}
              </span>
              <ChevronDown className={`w-3.5 h-3.5 text-[var(--content-tertiary)] transition-transform ${showSpacePicker ? 'rotate-180' : ''}`} />
            </button>
            {showSpacePicker && (
              <div className="mt-1 bg-[var(--surface-elevated)] border border-[var(--border-primary)] rounded-lg overflow-hidden animate-slideDown">
                <button
                  onClick={() => { setCurrentSpace(null); setShowSpacePicker(false); }}
                  className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left transition-colors ${
                    isPersonalSpace ? 'bg-[var(--accent-subtle)] text-[var(--accent-primary)] font-medium' : 'text-[var(--content-primary)] active:bg-[var(--sidebar-hover)]'
                  }`}
                >
                  {personalSpaceName}
                </button>
                {spaces.map(space => (
                  <button
                    key={space.id}
                    onClick={() => { setCurrentSpace(space); setShowSpacePicker(false); }}
                    className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left transition-colors ${
                      currentSpace?.id === space.id ? 'bg-[var(--accent-subtle)] text-[var(--accent-primary)] font-medium' : 'text-[var(--content-primary)] active:bg-[var(--sidebar-hover)]'
                    }`}
                  >
                    {space.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Search */}
        <div className="px-3 py-2">
          <div className="flex items-center gap-2 px-3 py-2 bg-[var(--surface-tertiary)] rounded-lg">
            <Search className="w-3.5 h-3.5 text-[var(--content-tertiary)] flex-shrink-0" />
            <input
              ref={searchRef}
              type="text"
              placeholder="Search events..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="flex-1 bg-transparent text-xs text-[var(--content-primary)] placeholder-[var(--content-tertiary)] outline-none"
            />
            {searchQuery && (
              <button onClick={() => onSearchChange('')} className="text-[var(--content-tertiary)]" aria-label="Clear search">
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-3 py-2 space-y-4">
          {/* Views */}
          <div>
            <div className="text-[10px] font-semibold text-[var(--content-tertiary)] uppercase tracking-widest px-2 mb-1">
              Views
            </div>
            <div className="space-y-0.5">
              {VIEW_MODES.map(({ mode, label, icon: Icon }) => (
                <button
                  key={mode}
                  onClick={() => { onViewModeChange(mode); onClose(); }}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                    viewMode === mode
                      ? 'bg-[var(--accent-subtle)] text-[var(--accent-primary)]'
                      : 'text-[var(--content-primary)] active:bg-[var(--sidebar-hover)]'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Categories */}
          {calendars.length > 0 && (
            <div>
              <div className="flex items-center justify-between px-2 mb-1">
                <span className="text-[10px] font-semibold text-[var(--content-tertiary)] uppercase tracking-widest">
                  My Calendars
                </span>
                <button
                  onClick={onSelectAllTags}
                  className="text-[10px] text-[var(--content-tertiary)] hover:text-[var(--accent-primary)] transition-colors"
                >
                  {allSelected ? 'Deselect all' : 'Select all'}
                </button>
              </div>
              <div className="space-y-0.5">
                {calendars.map(tag => {
                  const isSelected = selectedCalendarFilters.has(tag.id);

                  return (
                    <div key={tag.id} className="flex items-center gap-1">
                      <button
                        onClick={() => onToggleCalendarFilter(tag.id)}
                        className={`flex-1 flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                          isSelected
                            ? 'text-[var(--content-primary)]'
                            : 'text-[var(--content-tertiary)]'
                        } active:bg-[var(--sidebar-hover)]`}
                      >
                        <div
                          className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${tag.color} ${isSelected ? '' : 'opacity-40'}`}
                        />
                        <span className="text-sm font-medium truncate">{tag.name}</span>
                      </button>
                      {onOpenCategoryMenu && (
                        <button
                          onClick={(e) => onOpenCategoryMenu({ id: tag.id, name: tag.name, color: tag.color, is_shared: tag.is_shared, share_token: tag.share_token }, e.currentTarget, false)}
                          className="p-1.5 rounded-lg text-[var(--content-tertiary)] active:bg-[var(--sidebar-hover)] transition-colors flex-shrink-0"
                          aria-label={`Edit ${tag.name} category`}
                        >
                          <MoreVertical className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {/* Imported Calendars */}
          <div>
            <div className="flex items-center justify-between px-2 mb-1">
              <span className="text-[10px] font-semibold text-[var(--content-tertiary)] uppercase tracking-widest">
                Calendars
              </span>
              {onShowAddCalendarModal && (
                <button
                  onClick={() => { onShowAddCalendarModal(); onClose(); }}
                  className="text-[10px] text-[var(--content-tertiary)] hover:text-[var(--accent-primary)] transition-colors"
                >
                  +
                </button>
              )}
            </div>
            {importedCalendars && importedCalendars.length > 0 && (
              <div className="space-y-0.5">
                {importedCalendars.map(cal => (
                  <div key={cal.id} className="flex items-center gap-1">
                    <button
                      onClick={() => onToggleCalendar?.(cal.id)}
                      className={`flex-1 flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                        cal.enabled
                          ? 'text-[var(--content-primary)]'
                          : 'text-[var(--content-tertiary)]'
                      } active:bg-[var(--sidebar-hover)]`}
                    >
                      <span
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{
                          backgroundColor: cal.color || 'var(--content-tertiary)',
                          opacity: cal.enabled ? 1 : 0.4,
                        }}
                      />
                      <span className="text-sm font-medium truncate">{cal.name}</span>
                      {!cal.enabled && <EyeOff className="w-3 h-3 text-[var(--content-tertiary)] ml-auto flex-shrink-0" />}
                    </button>
                    {onOpenCalendarMenu && (
                      <button
                        onClick={(e) => onOpenCalendarMenu({ id: cal.id, name: cal.name, url: cal.url, color: cal.color }, e.currentTarget)}
                        className="p-1.5 rounded-lg text-[var(--content-tertiary)] active:bg-[var(--sidebar-hover)] transition-colors flex-shrink-0"
                        aria-label={`Edit ${cal.name} calendar`}
                      >
                        <MoreVertical className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-[var(--border-primary)] px-3 py-2 space-y-0.5">
          <button
            onClick={() => { onOpenSettings(); onClose(); }}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-[var(--content-primary)] active:bg-[var(--sidebar-hover)] transition-colors"
          >
            <Settings className="w-4 h-4" />
            <span className="text-sm font-medium">Settings</span>
          </button>
          {onSignOut && (
            <button
              onClick={() => { onSignOut(); onClose(); }}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-[var(--error)] active:bg-[var(--status-error-bg)] transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span className="text-sm font-medium">Sign out</span>
            </button>
          )}
        </div>
      </div>
    </>
  );
};
